import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import HighlightIcon from '@mui/icons-material/Highlight';
import NotesIcon from '@mui/icons-material/Notes';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { saveModuleProgress } from '../hooks/useProgress';

const NotesContainer = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '20px',
  padding: theme.spacing(3),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const NoteItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.1)',
  background: 'rgba(15, 15, 35, 0.5)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
    borderColor: '#667eea',
  },
}));

const HighlightChip = styled(Chip)(({ theme, highlightColor }) => ({
  backgroundColor: highlightColor || 'rgba(102, 126, 234, 0.2)',
  color: '#e2e8f0',
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  '& .MuiChip-icon': {
    color: '#e2e8f0',
  },
}));

const AddNoteButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  marginTop: theme.spacing(2),
  color: '#e2e8f0',
  borderColor: 'rgba(102, 126, 234, 0.3)',
  '&:hover': {
    borderColor: '#667eea',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  '&:disabled': {
    color: '#64748b',
    borderColor: 'rgba(102, 126, 234, 0.1)',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  color: '#cbd5e1',
}));

export default function StudyNotes({ moduleId, onHighlightText }) {
  const [notes, setNotes] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteType, setNoteType] = useState('note'); // 'note' or 'highlight'

  useEffect(() => {
    // Load saved notes and highlights
    const savedData = localStorage.getItem(`study-notes-${moduleId}`);
    if (savedData) {
      const { notes: savedNotes, highlights: savedHighlights } = JSON.parse(savedData);
      setNotes(savedNotes || []);
      setHighlights(savedHighlights || []);
    }
  }, [moduleId]);

  useEffect(() => {
    // Save notes and highlights
    const dataToSave = { notes, highlights };
    localStorage.setItem(`study-notes-${moduleId}`, JSON.stringify(dataToSave));
  }, [notes, highlights, moduleId]);

  useEffect(() => {
    // Listen for text selection
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();
      if (text) {
        setSelectedText(text);
      }
    };

    document.addEventListener('mouseup', handleSelection);
    return () => document.removeEventListener('mouseup', handleSelection);
  }, []);

  const handleAddNote = () => {
    if (newNote.trim() || selectedText.trim()) {
      const note = {
        id: Date.now(),
        content: newNote || selectedText,
        type: noteType,
        highlightedText: noteType === 'highlight' ? selectedText : null,
        createdAt: new Date().toISOString(),
        color: noteType === 'highlight' ? 'rgba(102, 126, 234, 0.2)' : null,
      };

      if (noteType === 'highlight' && selectedText) {
        setHighlights([...highlights, note]);
        // Callback to parent to highlight text in content
        if (onHighlightText) {
          onHighlightText(selectedText, note.color);
        }
      } else {
        setNotes([...notes, note]);
      }

      setNewNote('');
      setSelectedText('');
      setDialogOpen(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNote(note.content);
    setNoteType(note.type);
    setDialogOpen(true);
  };

  const handleUpdateNote = () => {
    if (editingNote) {
      if (editingNote.type === 'highlight') {
        setHighlights(highlights.map(h => 
          h.id === editingNote.id ? { ...h, content: newNote } : h
        ));
      } else {
        setNotes(notes.map(n => 
          n.id === editingNote.id ? { ...n, content: newNote } : n
        ));
      }
      setEditingNote(null);
      setNewNote('');
      setDialogOpen(false);
    }
  };

  const handleDeleteNote = (id, type) => {
    if (type === 'highlight') {
      setHighlights(highlights.filter(h => h.id !== id));
    } else {
      setNotes(notes.filter(n => n.id !== id));
    }
  };

  const allItems = [...notes, ...highlights].sort((a, b) => 
    new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <NotesContainer elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <NotesIcon sx={{ mr: 1, color: '#667eea' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f1f5f9' }}>
          Notas e Destaques
        </Typography>
      </Box>

      {allItems.length === 0 ? (
        <EmptyState>
          <NotesIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3, color: '#cbd5e1' }} />
          <Typography variant="body1" sx={{ color: '#e2e8f0' }}>
            Nenhuma nota ou destaque ainda
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#cbd5e1' }}>
            Selecione um texto para destacar ou clique em adicionar nota
          </Typography>
        </EmptyState>
      ) : (
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {allItems.map((item) => (
            <NoteItem key={item.id} elevation={0}>
              {item.type === 'highlight' && (
                <HighlightChip
                  icon={<HighlightIcon />}
                  label="Destaque"
                  size="small"
                  highlightColor={item.color}
                />
              )}
              {item.highlightedText && (
                <Box sx={{ mb: 1, p: 1, bgcolor: item.color || 'rgba(102, 126, 234, 0.2)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#e2e8f0' }}>
                    <FormatQuoteIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: '#e2e8f0' }} />
                    {item.highlightedText}
                  </Typography>
                </Box>
              )}
              <Typography variant="body1" sx={{ mb: 1, color: '#e2e8f0' }}>
                {item.content}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                  {new Date(item.createdAt).toLocaleString('pt-BR')}
                </Typography>
                <Box>
                  <IconButton size="small" onClick={() => handleEditNote(item)} sx={{ color: '#cbd5e1' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteNote(item.id, item.type)}
                    sx={{ color: '#ef4444' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </NoteItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 2, borderColor: 'rgba(102, 126, 234, 0.1)' }} />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <AddNoteButton
          variant="outlined"
          startIcon={<NotesIcon />}
          onClick={() => {
            setNoteType('note');
            setDialogOpen(true);
          }}
          fullWidth
        >
          Adicionar Nota
        </AddNoteButton>
        <AddNoteButton
          variant="outlined"
          startIcon={<HighlightIcon />}
          onClick={() => {
            setNoteType('highlight');
            setDialogOpen(true);
          }}
          fullWidth
          disabled={!selectedText}
        >
          Destacar
        </AddNoteButton>
      </Box>

      {/* Add/Edit Note Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false);
          setNewNote('');
          setEditingNote(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(102, 126, 234, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#f1f5f9' }}>
          {editingNote ? 'Editar' : 'Adicionar'} {noteType === 'highlight' ? 'Destaque' : 'Nota'}
        </DialogTitle>
        <DialogContent>
          {noteType === 'highlight' && selectedText && !editingNote && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(102, 126, 234, 0.2)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#e2e8f0' }}>
                <FormatQuoteIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: '#e2e8f0' }} />
                {selectedText}
              </Typography>
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={noteType === 'highlight' ? 'Comentário sobre o destaque' : 'Sua nota'}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={
              noteType === 'highlight' 
                ? 'Adicione um comentário sobre este destaque...' 
                : 'Digite sua nota aqui...'
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#e2e8f0',
                backgroundColor: '#0f0f23',
                '& fieldset': {
                  borderColor: 'rgba(102, 126, 234, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(102, 126, 234, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#cbd5e1',
                '&.Mui-focused': {
                  color: '#667eea',
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setNewNote('');
            setEditingNote(null);
          }} sx={{ color: '#cbd5e1' }}>
            Cancelar
          </Button>
          <Button 
            onClick={editingNote ? handleUpdateNote : handleAddNote}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#667eea',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#5a67d8',
              },
            }}
          >
            {editingNote ? 'Atualizar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </NotesContainer>
  );
}